import { ApolloError } from "@apollo/client";
import React, {
    ReactElement,
} from "react";
import classNames from "classnames";
import useRouting, { Paths } from "../../contexts/Routing";
import Styles from "./Error.module.scss";
import RouterLink from "../../components/theme/button/RouterLink";
import { useTheme } from "../../contexts/Theme";
import CodeBlock from "../../components/code-block/CodeBlock";

const ERROR_IMAGE = "https://static.foqal.io/agent/failbot.png";
const SECURITY_IMAGE = ERROR_IMAGE;

interface ErrorProps {
    message: string;
    title?: string;
    stacktrace?: string[] | undefined;
    image?: string;
    shouldShowLogin?: boolean;
    className?: string;
}

const DEFAULT_PROPS = {
    title: "Oops",
    stacktrace: undefined,
    image: ERROR_IMAGE,
    shouldShowLogin: false,
    className: "",
    pageClassName: "",
};

export function ErrorView(props: ErrorProps): ReactElement {
    const { historyPushWithSearch } = useRouting();
    const theme = useTheme();
    if (theme.isV3) {
        return (
            <div className={classNames(Styles["error-container"], props.className)}>
                <img
                    src={props.image ?? ERROR_IMAGE}
                    alt="error"
                    className={`me-2 ${Styles["error-image"]}`}
                />
                <h4 className={Styles.title}>{props.title}</h4>
                <p className={Styles["more-info"]}>
                    {props.message}
                </p>
                {props.stacktrace && (
                    <CodeBlock code={props.stacktrace.join("\n")} isCopyable />
                )}
                {props.shouldShowLogin && (
                    <RouterLink
                        to={Paths.LOGIN}
                        search={{ redirect: window.location.href }}
                        variant="outline-blue"
                    >
                        Login
                    </RouterLink>
                )}

            </div>
        );
    }
    return (
        <div className={`d-flex align-items-center w-100 ${props.className}`}>
            <img src={props.image ?? ERROR_IMAGE} alt="error" className={`me-2 ${Styles["error-image"]}`} />
            <div className="flex-grow-1 overflow-auto">
                <h3 className="mb-4">{props.title}</h3>
                <div className="font-light mb-3">{props.message}</div>
                {props.stacktrace && (
                    <div className="alert alert-secondary">
                        <pre className="text-start">
                            <code>
                                {props.stacktrace.join("\n")}
                            </code>
                        </pre>
                    </div>
                )}
                {props.shouldShowLogin && (
                    <button
                        type="button"
                        onClick={() => historyPushWithSearch(Paths.LOGIN, {
                            redirect: window.location.pathname,
                        })}
                        className="btn btn-primary px-4 btn"
                    >
                        Login
                    </button>
                )}
            </div>
        </div>
    );
}

ErrorView.defaultProps = {
    ...DEFAULT_PROPS,
};

export function ErrorDefault(props: ErrorProps & {pageClassName?: string}): ReactElement {
    const theme = useTheme();
    if (theme.isV3) {
        return (
            <div className="full-page">
                <div className={Styles["error-page"]}>
                    <ErrorView {...props} />
                </div>
            </div>
        );
    }
    return (
        <div className="full-page">
            <div className={`centered-container${props.stacktrace ? "-lg" : ""} shadow-lg border text-center ${
                props.pageClassName ?? "p-4"
            }`}
            >
                <ErrorView {...props} />
            </div>
        </div>
    );
}
ErrorDefault.defaultProps = {
    ...DEFAULT_PROPS,
};

export function Error404(): ReactElement {
    return (
        <ErrorDefault
            message="We have no idea how you are getting this page, but we don&apos;t know where it is either."
        />
    );
}

export function ErrorSecurityView(props: ErrorProps) {
    return (
        <ErrorView {...props} />
    );
}
ErrorSecurityView.defaultProps = {
    ...DEFAULT_PROPS,
    image: SECURITY_IMAGE,
    shouldShowLogin: true,
    title: "Not Authorized",
};

export function ErrorRemote({ error }: {error?: ApolloError}): ReactElement {
    const firstError = error?.graphQLErrors?.[0];
    const firstException = firstError?.extensions?.exception as Error & ({stacktrace: string[]}) | undefined;
    return (
        <ErrorDefault
            title={firstError?.message ?? "Internal Error"}
            message={firstException?.name ?? error?.message ?? "Error downloading from the server."}
            stacktrace={firstException?.stacktrace}
        />
    );
}
ErrorRemote.defaultProps = {
    error: undefined,
};

export function ErrorRemoteView({ error }: {error?: ApolloError}): ReactElement {
    const firstError = error?.graphQLErrors?.[0];
    const firstException = firstError?.extensions?.exception as Error & ({stacktrace: string[]}) | undefined;

    return (
        <ErrorView
            title={firstError?.message ?? "Internal Error"}
            message={firstException?.name ?? error?.message ?? "Error downloading from the server."}
            stacktrace={firstException?.stacktrace}
        />
    );
}
ErrorRemoteView.defaultProps = {
    error: undefined,
};

export function NoDataOrError({ error }: {error?: ApolloError}): ReactElement {
    return error ? <ErrorRemote error={error} /> : (
        <ErrorDefault
            title="Error Connecting"
            message="No data returned."
        />
    );
}
NoDataOrError.defaultProps = {
    error: undefined,
};

export function NoDataOrErrorView({ error }: {error?: ApolloError}): ReactElement {
    return error ? <ErrorRemoteView error={error} /> : (
        <ErrorView
            title="Error Connecting"
            message="No data returned."
        />
    );
}
NoDataOrErrorView.defaultProps = {
    error: undefined,
};
